import { Plus, Trash2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { ImageField } from "@/components/admin/Media";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export const PageTitle = ({ eyebrow, title, children }) => (
  <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <p className="eyebrow">{eyebrow}</p>
      <h1 className="mt-2 font-display text-3xl font-bold tracking-tight" data-testid="admin-page-title">{title}</h1>
    </div>
    {children && <div className="flex flex-wrap gap-3">{children}</div>}
  </div>
);

export const Card = ({ title, children, className, action }) => (
  <section className={cn("admin-card p-6", className)}>
    {(title || action) && (
      <div className="mb-5 flex items-center justify-between">
        {title && <h2 className="font-display text-base font-semibold uppercase tracking-[0.1em] text-slate-200">{title}</h2>}
        {action}
      </div>
    )}
    {children}
  </section>
);

export const Field = ({ label, hint, children, className }) => (
  <label className={cn("block", className)}>
    <span className="field-label">{label}</span>
    {children}
    {hint && <span className="mt-1.5 block text-xs text-slate-500">{hint}</span>}
  </label>
);

export const TextInput = (props) => <input {...props} className={cn("field", props.className)} />;
export const TextArea = (props) => <textarea {...props} className={cn("field resize-y", props.className)} />;
export const SelectInput = ({ children, ...props }) => <select {...props} className={cn("field appearance-none", props.className)}>{children}</select>;

export const Btn = ({ variant = "primary", className, ...props }) => (
  <button
    {...props}
    className={cn(
      "inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 font-display text-sm font-semibold transition-[background-color,color,opacity] duration-200 disabled:opacity-50",
      variant === "primary" && "bg-crimson text-white hover:bg-[#ff4d5a]",
      variant === "ghost" && "border border-white/15 text-slate-200 hover:border-white/40 hover:bg-white/5",
      variant === "danger" && "border border-crimson/40 text-crimson hover:bg-crimson/10",
      className,
    )}
  />
);

export const StatCard = ({ label, value, sub, testid }) => (
  <div className="admin-card p-5" data-testid={testid}>
    <p className="eyebrow">{label}</p>
    <p className="mt-3 font-display text-4xl font-extrabold tracking-tight">{value}</p>
    {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
  </div>
);

export const Badge = ({ children, tone = "slate" }) => (
  <span className={cn("inline-flex rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-[0.16em]",
    tone === "red" && "border-crimson/40 text-crimson", tone === "gold" && "border-gold/40 text-gold",
    tone === "green" && "border-emerald-500/40 text-emerald-400", tone === "slate" && "border-white/15 text-slate-400")}>
    {children}
  </span>
);

export const ConfirmDelete = ({ onConfirm, title = "Delete this item?", description = "This action cannot be undone.", testid, children, open, onOpenChange }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    {children && <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>}
    <AlertDialogContent className="border-white/10 bg-surface">
      <AlertDialogHeader>
        <AlertDialogTitle className="font-display">{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid={`${testid}-cancel`}>Cancel</AlertDialogCancel>
        <AlertDialogAction data-testid={`${testid}-confirm`} onClick={onConfirm} className="bg-crimson text-white hover:bg-[#ff4d5a]">Delete</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const ListEditor = ({ label, rows, onChange, fields, blank, testid, max }) => {
  const update = (i, key, val) => onChange(rows.map((r, idx) => (idx === i ? { ...r, [key]: val } : r)));
  const remove = (i) => onChange(rows.filter((_, idx) => idx !== i));
  const add = () => onChange([...rows, { ...blank }]);
  const hasImage = fields.some((f) => f.type === "image");
  const cols = hasImage ? "sm:grid-cols-2" : fields.length === 2 ? "sm:grid-cols-2" : fields.length === 3 ? "sm:grid-cols-3" : "sm:grid-cols-5";
  return (
    <div data-testid={testid}>
      <div className="mb-3 flex items-center justify-between">
        <span className="field-label !mb-0">{label}</span>
        <button type="button" onClick={add} disabled={max && rows.length >= max} data-testid={`${testid}-add`} className="inline-flex items-center gap-1 font-mono text-[11px] uppercase tracking-[0.16em] text-crimson disabled:opacity-40"><Plus className="h-3 w-3" /> Add</button>
      </div>
      <div className="space-y-2">
        {rows.map((row, i) => (
          <div key={i} className={cn("grid gap-2 sm:grid-cols-[1fr_auto]", hasImage && "rounded-md border border-white/10 p-2")} data-testid={`${testid}-row-${i}`}>
            <div className={cn("grid gap-2", cols)}>
              {fields.map((f) =>
                f.type === "image" ? (
                  <div key={f.key} className={cn("min-w-0 sm:col-span-2", f.className)}>
                    <ImageField compact value={row[f.key] ?? ""} onChange={(v) => update(i, f.key, v)} placeholder={f.placeholder} testid={`${testid}-${f.key}-${i}`} />
                  </div>
                ) : (
                  <input key={f.key} type={f.type || "text"} placeholder={f.placeholder} value={row[f.key] ?? ""} onChange={(e) => update(i, f.key, e.target.value)} className={cn("field !py-2 text-xs", f.className)} data-testid={`${testid}-${f.key}-${i}`} />
                ),
              )}
            </div>
            <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="flex h-9 w-9 items-center justify-center rounded-md border border-white/10 text-slate-400 hover:border-crimson hover:text-crimson"><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {!rows.length && <p className="text-xs text-slate-500">Nothing added yet.</p>}
      </div>
    </div>
  );
};
